import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { FlaskConical, Loader2, CloudOff, RefreshCw, Wifi, WifiOff, Eye, ChevronRight } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Panel } from "@/components/common/Panel";
import { EmptyState } from "@/components/common/EmptyState";
import { CockpitQuickView } from "@/components/common/CockpitQuickView";

const PLAN_CLS = {
  FREE: "bg-fundio-subtle text-fundio-muted border-fundio-border",
  LIGHT: "bg-status-info-bg text-status-info border-status-info-border",
  WEBPRO: "bg-fundio-gold/12 text-fundio-gold-deep border-fundio-gold/30",
};

function Val({ v }) {
  if (v === null || v === undefined || v === "")
    return <span className="text-xs italic text-fundio-faint">—</span>;
  return <span>{v}</span>;
}

export default function InterneQA() {
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [qvTenant, setQvTenant] = useState(null);

  const load = useCallback(() => {
    setState(null);
    api.get("/organizations", { params: { scope: "internal_qa" } })
      .then((r) => setState(r.data))
      .catch(() => setState({ organizations: [], canonical_service_available: false }));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Integrated in-CONTROL cockpit quick-view (no external redirect, no login switch).
  const openCockpit = (tenantId) => setQvTenant(tenantId);

  if (state === null)
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-fundio-gold" /></div>;

  const rows = state.organizations || [];
  const canonLive = state.canonical_service_available;

  return (
    <div data-testid="interne-qa-page">
      <PageHeader title="Interne QA" subtitle="Technische Test-/QA-Tenants für Regression & Security. Keine echten Kunden, keine Research-Daten." >
        <button data-testid="qa-refresh-button" onClick={load} className="inline-flex items-center gap-2 rounded-lg border border-fundio-border px-3 py-2 text-sm font-medium text-fundio-ink transition-colors hover:bg-fundio-subtle">
          <RefreshCw className="h-4 w-4" /> Aktualisieren
        </button>
      </PageHeader>

      <div data-testid="qa-scope-note" className="mb-4 flex items-center gap-2 rounded-lg border border-status-warning-border bg-status-warning-bg/60 px-4 py-2 text-xs text-status-warning">
        <FlaskConical className="h-4 w-4" />
        <span><span className="font-semibold">INTERNAL QA</span> · Diese Tenants sind ausschließlich für technische Tests. Sie erscheinen NICHT in „FUNDIO-Kunden" und verfälschen keine Business-KPIs.</span>
      </div>

      <div data-testid="qa-canonical-status" className={`mb-5 flex items-center gap-2 rounded-lg border px-4 py-2 text-xs ${canonLive ? "border-status-success-border bg-status-success-bg/50 text-status-success" : "border-fundio-border bg-fundio-subtle text-fundio-muted"}`}>
        {canonLive ? <Wifi className="h-4 w-4" /> : <WifiOff className="h-4 w-4" />}
        <span>{canonLive
          ? "Kanonischer Tenant-Service erreichbar – Live-Daten werden angezeigt."
          : `Kanonischer Tenant-Service nicht erreichbar – lokale QA-Kennzeichnung wird angezeigt. ${state.canonical_reason || ""}`}</span>
      </div>

      <Panel title="QA-/Test-Tenants" testid="qa-panel">
        {!rows.length ? (
          <EmptyState icon={CloudOff} title="Keine QA-Tenants" description="Es sind keine internen QA-Tenants markiert." testid="qa-list-empty" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-fundio-border text-left text-xs uppercase tracking-wide text-fundio-faint">
                  <th className="px-2 py-2 font-medium">Firma (Test)</th>
                  <th className="px-2 py-2 font-medium">tenant_id</th>
                  <th className="px-2 py-2 font-medium">Plan</th>
                  <th className="px-2 py-2 font-medium">environment_use</th>
                  <th className="px-2 py-2 font-medium">is_demo</th>
                  <th className="px-2 py-2 font-medium">Canonical</th>
                  <th className="px-2 py-2 font-medium">Aktion</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-fundio-border">
                {rows.map((o) => (
                  <tr key={o.tenant_id} data-testid={`qa-row-${o.tenant_id}`} className="transition-colors hover:bg-fundio-subtle">
                    <td className="px-2 py-3 font-medium text-fundio-ink">
                      {o.company_name}
                      <span data-testid={`qa-badge-${o.tenant_id}`} className="ml-2 inline-flex items-center rounded border border-status-warning-border bg-status-warning-bg px-1.5 py-0.5 font-mono text-[10px] font-semibold tracking-wider text-status-warning">INTERNAL QA</span>
                    </td>
                    <td className="px-2 py-3 font-mono text-xs text-fundio-muted">{o.tenant_id}</td>
                    <td className="px-2 py-3">
                      {o.plan ? <span className={`inline-flex rounded-md border px-2 py-0.5 text-xs font-semibold ${PLAN_CLS[o.plan] || PLAN_CLS.FREE}`}>{o.plan}</span> : <Val v={null} />}
                    </td>
                    <td className="px-2 py-3 font-mono text-xs text-fundio-muted">{o.environment_use}</td>
                    <td className="px-2 py-3 font-mono text-xs text-fundio-ink">{String(o.is_demo)}</td>
                    <td className="px-2 py-3">
                      {o.canonical_live
                        ? <span className="inline-flex items-center gap-1 text-xs text-status-success"><Wifi className="h-3.5 w-3.5" /> live</span>
                        : <span className="inline-flex items-center gap-1 text-xs text-fundio-faint"><WifiOff className="h-3.5 w-3.5" /> nur lokal</span>}
                    </td>
                    <td className="px-2 py-3">
                      <div className="flex items-center gap-2">
                        <button data-testid={`qa-detail-btn-${o.tenant_id}`} onClick={() => navigate(`/brockis/${o.tenant_id}`)}
                          className="inline-flex items-center gap-1 rounded-lg border border-fundio-border px-2.5 py-1.5 text-xs font-medium text-fundio-ink transition-colors hover:bg-fundio-subtle">
                          Detail öffnen <ChevronRight className="h-3.5 w-3.5" />
                        </button>
                        <button data-testid={`qa-cockpit-btn-${o.tenant_id}`} onClick={() => openCockpit(o.tenant_id)}
                          className="inline-flex items-center gap-1 rounded-lg border border-fundio-gold bg-fundio-dark2 px-2.5 py-1.5 text-xs font-semibold text-fundio-cream transition-colors hover:border-fundio-gold-highlight">
                          <Eye className="h-3.5 w-3.5" /> Cockpit ansehen
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {qvTenant && (
        <CockpitQuickView
          tenants={rows.map((o) => ({ tenant_id: o.tenant_id, company_name: o.company_name, plan: o.plan, account_mode: o.account_mode }))}
          initialTenantId={qvTenant}
          onClose={() => setQvTenant(null)}
        />
      )}
    </div>
  );
}
